import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import useTranslation from "../../hooks/useTranslation";
import {
  formatDateTime,
  formatReleaseNumber,
  releaseEntries,
} from "./product_logbook";

function TechnicalChangeCard({
  entry,
  entryNumber,
  translate,
}: {
  entry: (typeof releaseEntries)[number];
  entryNumber: number;
  translate: (key: string) => string;
}) {
  const releaseNumber = formatReleaseNumber(entryNumber);

  return (
    <div
      className="rounded-[2rem] border border-slate-200 bg-white/95"
      data-testid="technical-change-card"
    >
      <div className="p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          <span>{formatDateTime(entry.timestamp)}</span>
          <span className="h-1 w-1 rounded-full bg-slate-300" aria-hidden="true" />
          <span data-testid="technical-entry-number">
            {`${translate("product_updates_entry_label")} ${releaseNumber}`}
          </span>
        </div>
        <h3 className="mt-4 text-xl font-semibold leading-tight text-slate-900">
          {entry.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {entry.summary}
        </p>
        <div className="mt-5" data-testid="technical-notes-section">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {translate("product_updates_staff_section_title")}
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-600 list-disc pl-5">
            {entry.technicalNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TechnicalChangeLogPage() {
  const loginStatus = useLoginStatus();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const latestTimestamp = releaseEntries[0]?.timestamp ?? new Date().toISOString();
  const latestEntryNumber = releaseEntries.length;
  const isStaff =
    loginStatus.data?.state === "logged in" && loginStatus.data.is_staff;

  useEffect(() => {
    if (loginStatus.isSuccess && !isStaff) {
      navigate({ to: "/app/product_logbook", replace: true });
    }
  }, [isStaff, loginStatus.isSuccess, navigate]);

  if (!loginStatus.isSuccess || !isStaff) {
    return <div className="w-full px-4 py-6 sm:px-8 lg:px-12" />;
  }

  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-4xl space-y-10">
        <header className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {translate("technical_change_log_eyebrow")}
          </p>
          <div className="grid gap-4 rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/60 p-5 sm:grid-cols-[minmax(0,1fr)_11rem] sm:p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                  {translate("technical_change_log_title")}
                </h1>
                <p className="max-w-2xl text-sm leading-relaxed text-slate-600">
                  {translate("technical_change_log_description")}
                </p>
              </div>
              <div className="flex flex-wrap gap-5 text-sm text-slate-500">
                <p>{translate("product_updates_status_active")}</p>
                <p>
                  {`${translate("product_updates_last_update_label")} ${formatDateTime(
                    latestTimestamp,
                  )}`}
                </p>
              </div>
            </div>
            <div className="self-start rounded-[1.5rem] border border-slate-300 bg-white/90 p-4">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-slate-500">
                {translate("product_updates_current_label")}
              </p>
              <p
                className="mt-2 text-2xl font-semibold leading-none tracking-tight text-slate-900"
                data-testid="latest-technical-entry-number"
              >
                {`#${formatReleaseNumber(latestEntryNumber)}`}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {translate("technical_change_log_current_hint")}
              </p>
            </div>
          </div>
        </header>

        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            <h2 className="text-xl font-semibold text-slate-900">
              {translate("technical_change_log_section_title")}
            </h2>
          </div>
          <p className="text-sm text-slate-600">
            {translate("technical_change_log_section_description")}
          </p>
          <div className="space-y-4">
            {releaseEntries.map((entry, index) => (
              <TechnicalChangeCard
                key={entry.id}
                entry={entry}
                entryNumber={releaseEntries.length - index}
                translate={translate}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/technical_change_log")({
  component: TechnicalChangeLogPage,
});
